import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useStyles from 'hooks/useStyles';

import {
  getLiveTopic,
  getLiveTopicInfo,
  getLiveFilterInfo,
} from 'reduxModules/live/selector';
import { getUserIsAuthenticated } from 'reduxModules/user/selector';

import { openLiveModal } from 'reduxModules/live/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { push } from 'reduxModules/navigation/action';

import { SIGNUP_HINT } from 'routes/live/constants/modalName';
import { REGULAR_CLASS_MATERIAL_CLICK } from 'constants/gtmEvent';
import useLiveMainCheckoutCollectionUrl from 'routes/live/hooks/useLiveMainCheckoutCollectionUrl';

import MaterialButton from 'routes/live/components/MaterialButton';
import SeeMoreRow from 'components/SeeMoreRow';

import styles from './Materials.scss';

const DEFAULT_MATERIAL_COUNT = 2;

function Materials() {
  useStyles(styles);
  const { t } = useTranslation(['live']);

  const dispatch = useDispatch();

  const [showAllMaterials, setShowAllMaterials] = useState(false);
  const isAuthenticated = useSelector(getUserIsAuthenticated);
  const { selectedGradeLevel } = useSelector(getLiveFilterInfo);

  const { lessonMaterials, topicMaterials } = useSelector(getLiveTopic);
  const {
    isEnrolled,
    name: liveTopicName,
    id: liveTopicId,
    instructorNameList,
    instructorIdList,
  } = useSelector(getLiveTopicInfo);

  const materialList = [
    {
      lessonName: t('topic.materialsTitle'),
      materials: topicMaterials,
    },
    ...lessonMaterials,
  ];

  const partialMaterials = materialList.slice(0, DEFAULT_MATERIAL_COUNT);

  const toggleShowAllMaterials = () => setShowAllMaterials(prev => !prev);

  const singleTopicCheckoutCollectionUrl = useLiveMainCheckoutCollectionUrl();

  const onClickMaterial = ({ file, id, title, ordinalNumber }) => () => {
    if (!isAuthenticated) {
      dispatch(openLiveModal({ modalName: SIGNUP_HINT }));
      return;
    }

    if (!isEnrolled) {
      dispatch(push(singleTopicCheckoutCollectionUrl));
      return;
    }

    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_MATERIAL_CLICK,
        data: {
          topicId: liveTopicId,
          topicName: liveTopicName,
          gradeLevelName: selectedGradeLevel.name,
          instructorId: instructorIdList,
          instructorName: instructorNameList,
          materialId: id,
          materialName: title,
          itemIndex: ordinalNumber,
        },
      }),
    );
    window.open(file);
  };

  const renderMaterials = ({ lessonName: title, materials }, index) => {
    return (
      <React.Fragment key={`${title}-${index}`}>
        <h4 className={styles.subtitle}> {title} </h4>

        <div className={styles.materialsRow}>
          {materials.map((material, materialIndex) => (
            <MaterialButton
              className={styles.materialButton}
              {...material}
              ordinalNumber={materialIndex + 1}
              key={material.id}
              title={material.title}
              onClick={onClickMaterial}
              isDisabled={!material.downloadable}
            />
          ))}
        </div>
      </React.Fragment>
    );
  };

  const showSeeMoreRow =
    !showAllMaterials && materialList.length > DEFAULT_MATERIAL_COUNT;

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>{t(`topic.materials`)}</h2>
      {showSeeMoreRow
        ? partialMaterials.map(renderMaterials)
        : materialList.map(renderMaterials)}

      {showSeeMoreRow && (
        <SeeMoreRow
          onClick={toggleShowAllMaterials}
          className={styles.seeMore}
        />
      )}
    </section>
  );
}

export default Materials;
